/**
 * Dog list scalars for GET /api/dogs.
 * Omits deletedAt so Prisma does not SELECT a column that may be missing until
 * play_phase2 migration is applied on the shared Cloud SQL instance (P2022).
 */
export const DOG_LIST_SELECT_WITHOUT_DELETED_AT = {
  id: true,
  userId: true,
  name: true,
  breed: true,
  size: true,
  ageBracket: true,
  photoUrl: true,
  energyLevel: true,
  sociabilityDogs: true,
  sociabilityPeople: true,
  playStyles: true,
  leashBehavior: true,
  noiseSensitivity: true,
  crowdTolerance: true,
  weatherTolerance: true,
  surfaceSensitivity: true,
  carComfort: true,
  hasJointIssues: true,
  hasBreathingIssues: true,
  hasAllergies: true,
  allergyTags: true,
  traitsJson: true,
  createdAt: true,
  updatedAt: true,
} as const

/** Soft-fail codes that must not be treated as an authoritative empty dog list. */
export const DOGS_LIST_DB_UNAVAILABLE_CODES = new Set([
  'DB_ERROR',
  'DB_NOT_CONFIGURED',
  'DB_CONFIG_ERROR',
  'TABLE_NOT_EXISTS',
  'UNEXPECTED_ERROR',
])

export type DogsListApiPayload = {
  success?: boolean
  dogs?: unknown
  code?: string | null
  message?: string | null
}

export type DogsListInterpretation =
  | { kind: 'dogs'; dogs: unknown[] }
  | { kind: 'authoritative_empty' }
  | { kind: 'db_unavailable'; code: string; message: string | null }

/**
 * Classify GET /api/dogs JSON so DB soft-fails are never mistaken for zero dogs.
 */
export function interpretDogsListApiPayload(
  data: DogsListApiPayload | null | undefined
): DogsListInterpretation {
  const code = typeof data?.code === 'string' ? data.code : null
  if (code && DOGS_LIST_DB_UNAVAILABLE_CODES.has(code)) {
    return {
      kind: 'db_unavailable',
      code,
      message: typeof data?.message === 'string' ? data.message : null,
    }
  }

  if (!data?.success) {
    return {
      kind: 'db_unavailable',
      code: code ?? 'REQUEST_FAILED',
      message: typeof data?.message === 'string' ? data.message : null,
    }
  }

  const dogs = Array.isArray(data.dogs) ? data.dogs : []
  if (dogs.length === 0) {
    return { kind: 'authoritative_empty' }
  }
  return { kind: 'dogs', dogs }
}

export function buildDogsListFindManyArgs(userId: string) {
  return {
    // Exclude soft-deleted dogs so they cannot become the active Play dog.
    // Dog.deletedAt exists after play_phase2 migration; P2022 soft-fails in GET /api/dogs.
    where: { userId, deletedAt: null },
    orderBy: { createdAt: 'desc' as const },
    select: DOG_LIST_SELECT_WITHOUT_DELETED_AT,
  }
}

export function redactUserIdForLogs(userId: string | null | undefined): string {
  if (!userId) return 'null'
  return userId.length <= 8
    ? `${userId.slice(0, 4)}...`
    : `${userId.slice(0, 8)}...`
}

export function isPreviewCloudRunService(): boolean {
  return process.env.K_SERVICE === 'wagamour-preview'
}

export function logDogsApiGetDiagnostic(input: {
  userId: string | null | undefined
  resultCount: number
  prismaErrorCode?: string | null
  missingColumn?: string | null
}): void {
  if (!isPreviewCloudRunService()) return
  console.log('[DogsAPI][preview-diag]', {
    endpoint: '/api/dogs',
    userId: redactUserIdForLogs(input.userId),
    resultCount: input.resultCount,
    prismaErrorCode: input.prismaErrorCode ?? null,
    missingColumn: input.missingColumn ?? null,
  })
}
